/**
 * `?block=` — showing a blocked trigger as BLOCKED, DEGRADED or REDIRECTED.
 *
 * ```
 * ?block=cap:degraded                  the subscription cap, shown as a degrade
 * ?block=notadmin:blocked,otp:degraded two triggers at once
 * ?block=all:redirected                every one of the fourteen
 * ?block=off                           back to the catalogue's defaults
 * ```
 *
 * The trigger names are the short codes from the reference line — `flag`, `anon`,
 * `notadmin`, `ocn`, `company`, `cuifail`, `cuidown`, `elig`, `cap`, `nooffer`,
 * `msisdn`, `otp`, `stock`, `rate` — which are the prototype's own trigger ids, so
 * the URL, the support reference and the S-20 catalogue all name a trigger the same
 * way. Unrecognised pairs are dropped with a console warning; a typo must not look
 * like a switch that does nothing.
 *
 * ### Why this is the largest of the switches
 *
 * Eleven of the fourteen triggers have no outcome assigned in any Orange source.
 * `blocked-state.component.ts` carries a default for each with an `unspecified()`
 * marker against it, and those defaults are the POC's guesses — `NOT_ADMINISTRATOR`
 * as a redirect to assisted sales, `NO_ACTIVE_OCN` as a hard stop, and so on. Q-15
 * and Q-12 are exactly the questions of which it should be.
 *
 * A guess in a screenshot becomes a requirement inside a fortnight. A guess that can
 * be flipped to its two alternatives in front of the person who has to decide, while
 * they are looking at it, is a question being asked properly.
 *
 * ### It sets the override in two places
 *
 * `BlockOutcomes` is read by `orange-blocked-state` itself, which is what makes the
 * switch work on the four triggers a screen resolves locally (S-04's rejections,
 * S-05's exhausted OTP, S-07's and S-12's out-of-stock). The mock BFF's own
 * `BlockOutcomeOverrides` is what makes `EligibilityBlock.outcome` on the wire agree
 * with what is on the screen — and it has to, because `assistedSalesPhone` is only
 * populated when the wire says REDIRECTED. Forcing a redirect client-side alone
 * would produce a block that offers to call you back and shows no number.
 *
 * ### Persistence
 *
 * Held in `sessionStorage`, like `?rate=` and `?sla=`, because the query string does
 * not survive the first in-app navigation and most of these triggers fire two or
 * three screens after the one you can type a URL on. The underlying overrides are
 * not persisted — this re-applies them on every page load — so closing the tab is
 * enough to forget it, and `DemoControls.reset()` clears the BFF's copy.
 *
 * The dev panel sets `BlockOutcomes` and `MockBackend.controls.blockOutcomes` directly,
 * with no reload, and calls `setBlockSwitch` to hold the choice — `SwitchBoard.forceBlock`
 * is that pair. The URL form stays: the Playwright specs drive forced states by URL
 * rather than by clicking a panel.
 *
 * POC scaffolding. Production shows whichever outcome Orange chooses, once.
 */
import { inject, provideAppInitializer } from '@angular/core';
import type { EnvironmentProviders } from '@angular/core';
import type { BlockOutcome, BlockReason } from '../../generated/graphql';
import { MockBackend } from '../../mock-bff/mock-backend';
import { BlockOutcomes } from '../core/dev/block-outcomes';
import {
  blockCode,
  blockTriggers,
  reasonForCode,
} from '../ui/blocked-state/blocked-state.component';

export interface ForcedBlockOutcome {
  readonly reason: BlockReason;
  readonly outcome: BlockOutcome;
}

const STORAGE_KEY = 'pre2post.poc.block.v1';

/** Applies a held or URL-supplied switch at startup. */
export function provideBlockOutcomeSwitch(): EnvironmentProviders {
  return provideAppInitializer(() => {
    const forced = read();
    if (forced.length === 0) return;

    // Resolved through the injector rather than constructed: a second `MockBackend`
    // would be a second set of orders, and a second `BlockOutcomes` would be an
    // override nothing reads.
    const outcomes = inject(BlockOutcomes);
    const backend = inject(MockBackend);

    for (const { reason, outcome } of forced) {
      outcomes.set(reason, outcome);
      backend.controls.blockOutcomes.set(reason, outcome);
    }
  });
}

/** Holds the choice for the rest of the tab's life. Persists only; see the header. */
export function setBlockSwitch(forced: readonly ForcedBlockOutcome[]): void {
  try {
    if (forced.length === 0) sessionStorage.removeItem(STORAGE_KEY);
    else sessionStorage.setItem(STORAGE_KEY, formatBlockSwitch(forced));
  } catch {
    // Private browsing. The switch applies to this page load and no further.
  }
}

export function clearBlockSwitch(): void {
  setBlockSwitch([]);
}

/**
 * What is held right now.
 *
 * `BlockOutcomes.forced` is the live truth the components read, and the panel displays
 * that. This is what a reload would restore — the same distinction as
 * `currentAdapterModeSwitch`.
 */
export function currentBlockSwitch(): readonly ForcedBlockOutcome[] {
  try {
    return parseBlockSwitch(sessionStorage.getItem(STORAGE_KEY) ?? '');
  } catch {
    return [];
  }
}

export const formatBlockSwitch = (forced: readonly ForcedBlockOutcome[]): string =>
  forced.map((f) => `${blockCode(f.reason).toLowerCase()}:${f.outcome.toLowerCase()}`).join(',');

/**
 * `code:outcome` pairs, comma-separated. `all` stands for every trigger and `off`
 * for none, so the two things a demo actually does — sweep all fourteen, then put
 * them back — are each one word.
 */
export function parseBlockSwitch(value: string): readonly ForcedBlockOutcome[] {
  const trimmed = value.trim();
  if (trimmed === '' || trimmed === 'off' || trimmed === 'normal') return [];

  const forced: ForcedBlockOutcome[] = [];

  for (const pair of trimmed.split(',')) {
    const [rawReason = '', rawOutcome = ''] = pair.split(':');
    const outcome = parseOutcome(rawOutcome);

    if (outcome === null) {
      warn(pair);
      continue;
    }

    if (rawReason.trim().toLowerCase() === 'all') {
      for (const reason of blockTriggers()) forced.push({ reason, outcome });
      continue;
    }

    const reason = reasonForCode(rawReason);
    if (reason === null) {
      warn(pair);
      continue;
    }

    forced.push({ reason, outcome });
  }

  return forced;
}

const parseOutcome = (value: string): BlockOutcome | null => {
  const upper = value.trim().toUpperCase();
  return upper === 'BLOCKED' || upper === 'DEGRADED' || upper === 'REDIRECTED'
    ? (upper as BlockOutcome)
    : null;
};

/** The URL wins over the held value, and replaces it. */
function read(): readonly ForcedBlockOutcome[] {
  try {
    const fromUrl = new URLSearchParams(location.search).get('block');
    if (fromUrl !== null) {
      const parsed = parseBlockSwitch(fromUrl);
      // Persisted even when empty: `?block=off` has to be able to clear a held one.
      setBlockSwitch(parsed);
      return parsed;
    }
    return parseBlockSwitch(sessionStorage.getItem(STORAGE_KEY) ?? '');
  } catch {
    return [];
  }
}

const warn = (pair: string): void => {
  console.warn(
    `[dev] ?block= ignored "${pair}". Expected <trigger>:<blocked|degraded|redirected>, ` +
      `where trigger is one of ${blockTriggers().map(blockCode).join(', ').toLowerCase()} or all.`,
  );
};
